import React from "react";
import { Helmet } from "react-helmet-async";
import {
  SEO_DEFAULT_DESCRIPTION,
  SEO_DEFAULT_TITLE,
  SEO_SITE_NAME,
} from "constants/seo";
import { getCanonicalUrl, getDefaultOgImageUrl } from "utils/seo";

export interface PageSeoProps {
  title?: string;
  description?: string;
  /** Full document title without appending " | YourQcard". */
  fullTitle?: boolean;
  canonicalPath?: string;
  image?: string;
  imageAlt?: string;
  type?: "website" | "profile";
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const PageSeo: React.FC<PageSeoProps> = ({
  title,
  description = SEO_DEFAULT_DESCRIPTION,
  fullTitle = false,
  canonicalPath,
  image,
  imageAlt,
  type = "website",
  noindex = false,
  jsonLd,
}) => {
  const documentTitle = fullTitle
    ? title || SEO_DEFAULT_TITLE
    : title
      ? `${title} | ${SEO_SITE_NAME}`
      : SEO_DEFAULT_TITLE;

  const canonical = getCanonicalUrl(canonicalPath ?? (typeof window !== "undefined" ? window.location.pathname : "/"));
  const ogImage = image || getDefaultOgImageUrl();
  const ogImageAlt = imageAlt || SEO_SITE_NAME;
  const robots = noindex ? "noindex, nofollow" : "index, follow";

  return (
    <Helmet>
      <title>{documentTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SEO_SITE_NAME} />
      <meta property="og:title" content={documentTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={documentTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
};

export default PageSeo;
